import { useSplitLayout } from '@components/app/split-layout/layout';
import { EntityIcon } from '@core/component/EntityIcon';
import { ItemPreview } from '@core/component/ItemPreview';
import CaretRight from '@icon/system/regular/caret-right.svg';
import PaperPlaneTilt from '@icon/system/regular/paper-plane-tilt.svg';
import type { NamedTool } from '@service-cognition/generated/tools/tool';
import type { SendEmail } from '@service-cognition/generated/tools/types';
import { cn } from '@ui';
import { Match, Show, Suspense, Switch } from 'solid-js';
import { BaseTool } from './BaseTool';
import { ComposeTool } from './email/ChatCompose';
import { createToolRenderer } from './ToolRenderer';

type SendEmailResponse = NamedTool<'SendEmail', 'response'>['data'];

function getRecipientsLabel(data: SendEmail) {
  const recipients = [
    ...(data.to ?? []),
    ...(data.cc ?? []),
    ...(data.bcc ?? []),
  ];
  const labels = Array.from(
    new Map(
      recipients.map((recipient) => [
        recipient.email,
        recipient.name?.trim() || recipient.email,
      ])
    ).values()
  );

  if (labels.length === 0) return 'recipient';
  if (labels.length === 1) return labels[0]!;
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels[0]}, ${labels[1]}, +${labels.length - 2} more`;
}

function getAttachmentsLabel(data: SendEmail) {
  const count = data.attachments?.length ?? 0;
  if (count === 0) return undefined;
  return `${count} ${count === 1 ? 'attachment' : 'attachments'}`;
}

function getUserAction(response: SendEmailResponse | undefined) {
  if (
    typeof response === 'object' &&
    response !== null &&
    'UserAction' in response
  ) {
    return response.UserAction;
  }

  return null;
}

function getSentResponse(response: SendEmailResponse | undefined) {
  const userAction = getUserAction(response);
  if (
    typeof userAction === 'object' &&
    userAction !== null &&
    'sent' in userAction
  ) {
    return userAction.sent;
  }

  return null;
}

function getDraftResponse(response: SendEmailResponse | undefined) {
  const userAction = getUserAction(response);
  if (
    typeof userAction === 'object' &&
    userAction !== null &&
    'convertedToDraft' in userAction
  ) {
    const draft = userAction.convertedToDraft;
    if (
      typeof draft === 'object' &&
      draft !== null &&
      'draft_id' in draft &&
      typeof draft.draft_id === 'string'
    ) {
      return {
        draft_id: draft.draft_id,
        thread_id:
          'thread_id' in draft && typeof draft.thread_id === 'string'
            ? draft.thread_id
            : undefined,
      };
    }
  }

  return null;
}

function DraftPreviewButton(props: {
  draftId: string;
  subject: string;
  threadId?: string;
}) {
  const { openWithSplit } = useSplitLayout();

  return (
    <button
      class="text-ink text-xs border border-edge-muted rounded-xs hover:bg-hover flex flex-row h-6 px-2 justify-center items-center"
      onClick={(event) =>
        openWithSplit(
          {
            ...(props.threadId
              ? { type: 'email' as const, id: props.threadId }
              : {
                  type: 'component' as const,
                  id: 'email-compose',
                  params: { draftID: props.draftId },
                }),
          },
          { activate: true, preferNewSplit: event.shiftKey }
        )
      }
    >
      <div class="flex justify-start items-center size-3.5 mr-2">
        <EntityIcon targetType="email" size="xs" />
      </div>
      <div class="flex-1 text-left leading-5 min-w-0 truncate">
        {props.subject || 'Draft email'}
      </div>
    </button>
  );
}

export function SentEmailResponse(props: {
  args: SendEmail;
  chatId: string;
  messageId: string;
  renderContext: Parameters<typeof BaseTool>[0]['renderContext'];
  threadId: string;
  toolCallId: string;
}) {
  return (
    <details class="group">
      <summary class="list-none [&::-webkit-details-marker]:hidden">
        <BaseTool renderContext={props.renderContext} type="response">
          <div class="flex items-center justify-between gap-3 text-xs text-ink-extra-muted">
            <div class="flex min-w-0 flex-wrap items-center gap-2">
              <span>
                Email sent to{' '}
                <span class="text-ink">{getRecipientsLabel(props.args)}</span>
              </span>
              <Show when={props.args.from?.trim()}>
                {(from) => (
                  <span>
                    from <span class="text-ink">{from()}</span>
                  </span>
                )}
              </Show>
              <Show when={getAttachmentsLabel(props.args)}>
                {(label) => <span>with {label()}</span>}
              </Show>
              <Suspense>
                <ItemPreview class="ring-0" id={props.threadId} type="email" />
              </Suspense>
            </div>
            <span class="shrink-0 text-ink-muted">
              <CaretRight class={cn('size-4', 'group-open:rotate-90')} />
            </span>
          </div>
        </BaseTool>
      </summary>
      <div class="mt-3">
        <ComposeTool
          chatId={props.chatId}
          initialData={props.args}
          messageId={props.messageId}
          toolCallId={props.toolCallId}
          readOnly
          header={<div class="text-xs text-ink-extra-muted/60">Email sent</div>}
        />
      </div>
    </details>
  );
}

/** A send with no result: still in flight, or failed. */
export function SendEmailCall(props: {
  args: SendEmail;
  renderContext: Parameters<typeof BaseTool>[0]['renderContext'];
}) {
  return (
    <BaseTool
      icon={PaperPlaneTilt}
      renderContext={props.renderContext}
      type="call"
    >
      <span class="min-w-0 truncate">
        Send email to{' '}
        <span class="text-ink">{getRecipientsLabel(props.args)}</span>
      </span>
    </BaseTool>
  );
}

export function DraftEmailResponse(props: {
  args: SendEmail;
  draftId: string;
  renderContext: Parameters<typeof BaseTool>[0]['renderContext'];
  threadId?: string;
}) {
  return (
    <BaseTool renderContext={props.renderContext} type="response">
      <div class="flex items-center justify-between gap-3 text-xs text-ink-extra-muted">
        <div class="flex min-w-0 flex-wrap items-center gap-2">
          <span>
            Email saved as draft for{' '}
            <span class="text-ink">{getRecipientsLabel(props.args)}</span>
          </span>
          <Show when={getAttachmentsLabel(props.args)}>
            {(label) => <span>with {label()}</span>}
          </Show>
          <DraftPreviewButton
            draftId={props.draftId}
            subject={props.args.subject}
            threadId={props.threadId}
          />
        </div>
      </div>
    </BaseTool>
  );
}

const handler = createToolRenderer({
  name: 'SendEmail',
  render: (ctx) => {
    const response = () => ctx.response?.data;
    const args = ctx.tool.data;
    const userAction = getUserAction(response());
    const sentResponse = getSentResponse(response());
    const draftResponse = getDraftResponse(response());

    return (
      <Show when={ctx.response}>
        <Switch>
          <Match
            when={
              response() === 'PendingUserExecution' ||
              userAction === 'userEdited'
            }
          >
            <ComposeTool
              chatId={ctx.chat_id}
              initialData={ctx.tool.data}
              messageId={ctx.message_id}
              toolCallId={ctx.tool.id}
              streamLocked={ctx.renderContext.isStreaming}
              header={
                ctx.renderContext.isStreaming ? (
                  <div class="text-xs text-ink-extra-muted/60">
                    Waiting for the response to finish before this email can be
                    edited.
                  </div>
                ) : undefined
              }
            />
          </Match>
          <Match when={response() === 'Rejected'}>
            <BaseTool renderContext={ctx.renderContext} type="response">
              Email send rejected
            </BaseTool>
          </Match>
          <Match when={sentResponse}>
            <SentEmailResponse
              args={args}
              chatId={ctx.chat_id}
              messageId={ctx.message_id}
              renderContext={ctx.renderContext}
              threadId={sentResponse!.thread_id}
              toolCallId={ctx.tool.id}
            />
          </Match>
          <Match when={draftResponse}>
            <DraftEmailResponse
              args={args}
              draftId={draftResponse!.draft_id}
              renderContext={ctx.renderContext}
              threadId={draftResponse!.thread_id}
            />
          </Match>
        </Switch>
      </Show>
    );
  },
});

export const sendEmailHandler = handler;
